import { t, Trans } from '@lingui/macro';
import { invalidateQueryExactMatch, noddiAsync, URLKeys } from 'noddi-async';
import { tracking } from 'noddi-provider';
import { BookingAddress, BookingDetails } from 'noddi-types';
import { NoddiButton } from 'noddi-ui-common';
import { NoddiBasicCard } from 'noddi-ui-web';
import { customerRoutes } from 'noddi-util';
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ContactUs } from '../../../components/ContactUs';
import { ErrorPageWithTranslations } from '../../../components/ErrorPageWithTrans';
import {
  NoddiLoadingScreenUpdateBooking,
  NoddiLoadingScreenWithTrans
} from '../../../components/NoddiLoadingScreenWithTrans';
import { RecallContentWrapper } from '../../Recall/ContentWrapper';
import { EditAddressPicker } from './EditAddressPicker';

export const EditAddress = () => {
  const { id } = useParams();

  const {
    isPending: isBookingPending,
    data: bookingData,
    error
  } = noddiAsync.useGet({
    type: URLKeys.getUserBooking,
    input: { bookingId: parseInt(id!) }
  });

  if (isBookingPending) {
    return <NoddiLoadingScreenWithTrans />;
  }

  if (error) {
    return <ErrorPageWithTranslations apiError={[error]} />;
  }

  return <EditAddressContent booking={bookingData} />;
};

const EditAddressContent = ({ booking }: { booking: BookingDetails }) => {
  const { address, id } = booking;
  const navigate = useNavigate();
  const [selectedAddress, setSelectedAddress] = useState<BookingAddress>();

  const {
    mutateAsync: updateBooking,
    isPending: isUpdateBookingPending,
    error: updateBookingError
  } = noddiAsync.usePatch({
    type: URLKeys.patchBooking,
    queryConfig: {
      onSuccess: async () => {
        tracking.track('editBookingAddress');
        await invalidateQueryExactMatch({
          urlKey: URLKeys.getUserBooking,
          input: { bookingId: id }
        });
        navigate(customerRoutes.myBookingDetails.getPath({ id }));
      }
    }
  });

  const {
    data: potentialConflicts,
    isLoading: isPotentialConflictsLoading,
    error: errorPotentialConflicts
  } = noddiAsync.useGet({
    type: URLKeys.getPotentialConflictsOnAddressChange,
    input: {
      bookingId: id,
      addressId: selectedAddress?.id
    },
    queryConfig: {
      enabled: !!selectedAddress?.id
    }
  });

  const canUpdateAddress =
    selectedAddress &&
    id &&
    // Dont use ? operator here, because we want to make sure potentialConflicts is defined and the data is falsy
    potentialConflicts &&
    !potentialConflicts.conflictingSalesItems.length &&
    !potentialConflicts.isDeliveryWindowConflicting;

  if (updateBookingError || errorPotentialConflicts) {
    return <ErrorPageWithTranslations apiError={[updateBookingError, errorPotentialConflicts]} />;
  }

  if (isUpdateBookingPending) {
    return <NoddiLoadingScreenUpdateBooking />;
  }

  const areConflictsLoadedAndPresent =
    !!potentialConflicts?.conflictingSalesItems.length || potentialConflicts?.isDeliveryWindowConflicting;

  const hasConflictingSalesItems = !!potentialConflicts?.conflictingSalesItems.length;

  return (
    <RecallContentWrapper title={t`Where do you need our help?`}>
      <div className='flex flex-col gap-4'>
        <EditAddressPicker
          bookingId={id}
          addressId={address.id}
          selectedAddress={selectedAddress}
          hideSavedAddresses={!!areConflictsLoadedAndPresent}
          setSelectedAddress={async (address) => {
            setSelectedAddress(address);
          }}
          loading={isPotentialConflictsLoading}
        />
        {canUpdateAddress && (
          <NoddiButton
            className='w-full'
            onPress={async () => {
              await updateBooking({
                id,
                addressId: selectedAddress.id
              });
            }}
          >
            <Trans>Update address</Trans>
          </NoddiButton>
        )}
        {hasConflictingSalesItems && (
          <NoddiBasicCard className='flex flex-col gap-4'>
            <div className='flex flex-col gap-1'>
              <span>
                <Trans>Some items in your booking cannot be delivered to this address</Trans>🥲{' '}
                <Trans>
                  If you still wish to change, please create a new booking with different items and cancel this one.
                </Trans>{' '}
                <ContactUs />
              </span>
            </div>
            <span className='font-bold'>
              <Trans>Unavailable items</Trans> :
            </span>
            <ul className='flex flex-col gap-1'>
              {potentialConflicts.conflictingSalesItems.map((item, idx) => (
                <li key={idx}>
                  <p>{item.name}</p>
                </li>
              ))}
            </ul>
          </NoddiBasicCard>
        )}
        {!hasConflictingSalesItems && !!potentialConflicts?.isDeliveryWindowConflicting && (
          <NoddiBasicCard className='flex flex-col gap-4'>
            <div className='flex flex-col gap-1'>
              <span>
                <Trans>Your delivery window is not available in this address</Trans>🥲{' '}
                <Trans>Please create a new booking and cancel this one.</Trans> <ContactUs />
              </span>
            </div>
          </NoddiBasicCard>
        )}
      </div>
    </RecallContentWrapper>
  );
};
